import React, { createContext, useContext, useState, useEffect } from 'react';
import yaml from 'js-yaml';

const LocalizationContext = createContext();

export const useLocalization = () => {
  const context = useContext(LocalizationContext);
  if (!context) {
    throw new Error('useLocalization must be used within LocalizationProvider');
  }
  return context;
};

export const LocalizationProvider = ({ children }) => {
  const [language, setLanguage] = useState(null); // Start with null to determine language properly
  const [translations, setTranslations] = useState({});
  const [loading, setLoading] = useState(true);

  // Initialize language on mount with proper detection
  useEffect(() => {
    const initializeLanguage = () => {
      console.log('🌐 Initializing language...');
      
      // 1. Check localStorage first
      const savedLanguage = localStorage.getItem('preferred-language');
      console.log('📱 Saved language from localStorage:', savedLanguage);
      
      // 2. Detect browser language as fallback
      const browserLanguage = navigator.language.split('-')[0]; // e.g., 'en-US' -> 'en'
      console.log('🌍 Browser language detected:', browserLanguage);
      
      // 3. Validate and choose language
      const supportedLanguages = ['hr', 'en', 'nl'];
      let selectedLanguage = 'en'; // Default fallback
      
      if (savedLanguage && supportedLanguages.includes(savedLanguage)) {
        selectedLanguage = savedLanguage;
        console.log('✅ Using saved language:', selectedLanguage);
      } else if (supportedLanguages.includes(browserLanguage)) {
        selectedLanguage = browserLanguage;
        console.log('✅ Using browser language:', selectedLanguage);
      } else {
        console.log('✅ Using default language:', selectedLanguage);
      }
      
      console.log('🎯 Final language selection:', selectedLanguage);
      setLanguage(selectedLanguage);
    };

    initializeLanguage();
  }, []);

  useEffect(() => {
    if (language) {
      console.log('🔄 Loading translations for language:', language);
      loadTranslations(language);
    }
  }, [language]);

  const loadTranslations = async (lang) => {
    if (!lang) return;
    
    setLoading(true);
    console.log('📥 Fetching translations for:', lang);
    
    try {
      const response = await fetch(`/app/locales/${lang}.yaml`);
      if (!response.ok) {
        throw new Error(`Failed to fetch translations: ${response.status}`);
      }
      
      const yamlText = await response.text();
      const translations = yaml.load(yamlText);
      
      console.log('✅ Translations loaded successfully for:', lang);
      console.log('📊 Translation keys loaded:', Object.keys(translations).length);
      
      // Debug specific keys
      if (translations.result?.levelUp) {
        console.log('✅ result.levelUp translation found:', translations.result.levelUp);
      } else {
        console.warn('⚠️ result.levelUp translation NOT found');
      }
      
      setTranslations(translations);
    } catch (error) {
      console.error('❌ Failed to load translations for', lang, ':', error);
      
      // If loading fails and we're not on the default language, try fallback
      if (lang !== 'en') {
        console.log('🔄 Falling back to English translations...');
        setLanguage('en');
      } else {
        // If even default fails, use empty object
        console.warn('⚠️ Using empty translations as final fallback');
        setTranslations({});
      }
    }
    setLoading(false);
  };

  const t = (key, params = {}) => {
    // If we're still loading or language is not set, return the key
    if (!language || loading) {
      return key;
    }
    
    const keys = key.split('.');
    let value = translations;
    
    for (const k of keys) {
      value = value?.[k];
      if (value === undefined) {
        console.warn(`🔍 Translation missing for key: ${key} (language: ${language})`);
        return key; // Return key if translation not found
      }
    }
    
    // Handle parameter substitution
    if (typeof value === 'string' && Object.keys(params).length > 0) {
      return value.replace(/\{(\w+)\}/g, (match, param) => {
        return params[param] || match;
      });
    }
    
    return value || key;
  };

  const changeLanguage = (newLanguage) => {
    console.log('🔄 Language change requested:', language, '->', newLanguage);
    
    // Validate new language
    const supportedLanguages = ['hr', 'en', 'nl'];
    if (!supportedLanguages.includes(newLanguage)) {
      console.error('❌ Unsupported language requested:', newLanguage);
      return;
    }
    
    // Don't change if it's the same language
    if (language === newLanguage) {
      console.log('ℹ️ Language already set to:', newLanguage);
      return;
    }
    
    console.log('💾 Saving language preference to localStorage:', newLanguage);
    localStorage.setItem('preferred-language', newLanguage);
    
    console.log('🎯 Setting new language:', newLanguage);
    setLanguage(newLanguage);
  };

  const value = {
    language,
    translations,
    loading,
    t,
    changeLanguage,
    availableLanguages: [
      { code: 'hr', name: 'Hrvatski' },
      { code: 'en', name: 'English' },
      // Flemish Dutch - shown as Belgium (never "Netherlands"): the app's Dutch
      // audience is Belgian children, so the label uses their own wording
      { code: 'nl', name: 'Nederlands (België)' }
    ],
    // Debug info (can be used by components to show current state)
    debug: {
      translationKeys: Object.keys(translations).length,
      isInitialized: language !== null,
      hasTranslations: Object.keys(translations).length > 0
    }
  };

  return (
    <LocalizationContext.Provider value={value}>
      {children}
    </LocalizationContext.Provider>
  );
};